import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Plus, Save } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  card,
  cardTitle,
  bodySecondary,
  surfaceMuted,
  dividerInner,
  iconSize,
  motionFadeUp,
} from '../../../lib/design-system';
import { cn } from '../../../lib/utils';

const inputClasses =
  "w-full rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1/50 focus:border-transparent p-3 text-sm";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const btnSecondary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const AddQuestionModal = ({
  isOpen,
  onClose,
  onSave,
  courseData,
  bloomLevels,
  getCourseOutcomes,
  formatOutcomeForDisplay,
  extractOutcomeCode,
  resolveV2Id,
  totalPoints = 100,
  isUngraded = false,
  currentTotalPoints = 0,
}) => {
  const [question, setQuestion] = useState('');
  const [points, setPoints] = useState(10);
  const [bloomLevel, setBloomLevel] = useState('');
  const [courseOutcome, setCourseOutcome] = useState('');

  // Set points to 0 when assignment is ungraded, or reset to default when modal opens for graded assignment
  useEffect(() => {
    if (isUngraded) {
      setPoints(0);
    } else if (isOpen && !isUngraded && points === 0) {
      // Reset to default when opening modal for graded assignment
      setPoints(10);
    }
  }, [isUngraded, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!question.trim()) {
      alert('Please enter a question');
      return;
    }

    // For ungraded assignments, always use 0 points
    const questionPoints = isUngraded ? 0 : (points || 10);

    // Only validate if assignment is not ungraded
    if (!isUngraded && totalPoints > 0) {
      const newTotal = currentTotalPoints + questionPoints;

      if (newTotal > totalPoints) {
        const remaining = totalPoints - currentTotalPoints;
        if (remaining <= 0) {
          alert(
            `Cannot add question. Total points already reached (${currentTotalPoints}/${totalPoints}). Please adjust question points or total assignment points.`
          );
          return;
        }
        const confirm = window.confirm(
          `Warning: Adding this question will exceed the total points (${newTotal} > ${totalPoints}).\n\n` +
          `Current total: ${currentTotalPoints} points\n` +
          `This question: ${questionPoints} points\n` +
          `Remaining points available: ${remaining} points\n\n` +
          `Would you like to proceed anyway?`
        );
        if (!confirm) {
          return;
        }
      }
    }

    const coCode = extractOutcomeCode(courseOutcome) || undefined;
    const coV2Id = resolveV2Id ? resolveV2Id(coCode) : undefined;
    const newQuestion = {
      id: `manual_${Date.now()}`,
      question: question.trim(),
      type: 'subjective',
      points: questionPoints,
      score: questionPoints,
      bloomLevel: bloomLevel || undefined,
      courseOutcome: coCode,
      ...(coV2Id ? { courseOutcomeId: coV2Id } : {}),
      source: 'manual',
      status: 'approved',
    };

    onSave(newQuestion);

    // Reset form
    setQuestion('');
    setPoints(isUngraded ? 0 : 10);
    setBloomLevel('');
    setCourseOutcome('');
    onClose();
  };

  const handleCancel = () => {
    setQuestion('');
    setPoints(isUngraded ? 0 : 10);
    setBloomLevel('');
    setCourseOutcome('');
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={handleCancel}
      />

      {/* Modal panel */}
      <motion.div
        {...motionFadeUp}
        className={cn(
          card,
          "relative w-full max-w-2xl max-h-[95vh] flex flex-col shadow-xl mx-4"
        )}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between p-6 border-b", dividerInner)}>
          <div className="flex items-center gap-3">
            <Plus className={cn("text-accent1", iconSize.lg)} />
            <h2 className={cn(cardTitle, "text-lg")}>Add Question</h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close modal"
          >
            <X className={iconSize.md} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-5">
            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Question Text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className={inputClasses}
                rows="6"
                placeholder="Enter your question here..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Points */}
              <div>
                <label
                  className={cn(
                    "block text-sm font-medium mb-2",
                    isUngraded
                      ? "text-gray-400 dark:text-gray-600"
                      : "text-gray-700 dark:text-gray-300"
                  )}
                >
                  Points
                  {isUngraded && (
                    <span className={cn("text-xs ml-2", bodySecondary)}>
                      (Ungraded — disabled)
                    </span>
                  )}
                  {!isUngraded && totalPoints > 0 && (
                    <span className={cn("text-xs ml-2", bodySecondary)}>
                      (Remaining: {totalPoints - currentTotalPoints} pts)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => {
                    if (!isUngraded) {
                      setPoints(Number(e.target.value));
                    }
                  }}
                  min="0"
                  max={
                    !isUngraded && totalPoints > 0
                      ? totalPoints - currentTotalPoints
                      : undefined
                  }
                  disabled={isUngraded}
                  className={cn(
                    inputClasses,
                    isUngraded
                      ? "bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-gray-700"
                      : ""
                  )}
                />
                {isUngraded ? (
                  <p className={cn("text-xs mt-1 italic", bodySecondary)}>
                    This assignment is ungraded — points are set to 0
                  </p>
                ) : (
                  totalPoints > 0 && (
                    <p className={cn("text-xs mt-1", bodySecondary)}>
                      Total: {currentTotalPoints} / {totalPoints} points
                    </p>
                  )
                )}
              </div>

              {/* Bloom Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bloom Level
                </label>
                <select
                  value={bloomLevel}
                  onChange={(e) => setBloomLevel(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Select level</option>
                  {bloomLevels.map((level) => (
                    <option key={level} value={level.toLowerCase()}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              {/* Course Outcome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Course Outcome
                </label>
                {getCourseOutcomes().length > 0 ? (
                  <select
                    value={courseOutcome}
                    onChange={(e) => setCourseOutcome(e.target.value)}
                    className={inputClasses}
                  >
                    <option value="">Select course outcome</option>
                    {getCourseOutcomes().map((outcome, index) => {
                      const displayText = formatOutcomeForDisplay(outcome);
                      const code = extractOutcomeCode(outcome);
                      return (
                        <option key={index} value={code}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div className="w-full p-3 rounded-sm border border-yellow-300 dark:border-yellow-700/50 bg-yellow-50 dark:bg-yellow-900/20">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      No course outcomes found. Please add course outcomes in course settings.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={cn(
            "flex justify-end gap-3 p-6 border-t",
            surfaceMuted,
            dividerInner
          )}
        >
          <button onClick={handleCancel} className={btnSecondary}>
            Cancel
          </button>
          <button onClick={handleSave} className={btnPrimary}>
            <Save className={iconSize.md} />
            Add Question
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default AddQuestionModal;
